import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { WatermarkSettings } from '../../services/api';
import { buildWatermarkText } from '../../utils/watermarkUtils';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { RectangleStackIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import AlignmentControl from './AlignmentControl';

interface WatermarkPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  watermark: WatermarkSettings;
  type: 'screen' | 'print';
  onWatermarkChange?: (watermark: WatermarkSettings) => void;
}

const WatermarkPreview: React.FC<WatermarkPreviewProps> = ({ 
  isOpen, 
  onClose, 
  watermark, 
  type,
  onWatermarkChange 
}) => {
  const { t } = useTranslation('settings');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [localWatermark, setLocalWatermark] = useState<WatermarkSettings>(watermark);
  const modalRef = useRef<HTMLDivElement>(null);

  // Check if mobile (viewport width < 768px) - MUST be before early return
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Sync local watermark with prop changes
  useEffect(() => {
    setLocalWatermark(watermark);
  }, [watermark]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Notify header when preview opens/closes
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('watermark-preview-opened'));
    } else {
      window.dispatchEvent(new CustomEvent('watermark-preview-closed'));
    }
    
    return () => {
      if (isOpen) {
        window.dispatchEvent(new CustomEvent('watermark-preview-closed'));
      }
    };
  }, [isOpen]);

  // Update parent when local watermark changes
  const updateWatermark = (updates: Partial<WatermarkSettings>) => {
    const updated = { ...localWatermark, ...updates };
    setLocalWatermark(updated);
    if (onWatermarkChange) {
      onWatermarkChange(updated);
    }
  };

  // Handle text config changes
  const handleTextConfigChange = (key: keyof typeof localWatermark.text_config, value: boolean | string) => {
    const updatedTextConfig = { ...localWatermark.text_config, [key]: value };
    
    // If custom_text is enabled, disable all other options
    if (key === 'custom_text' && value === true) {
      Object.keys(updatedTextConfig).forEach((k) => {
        if (k !== 'custom_text' && k !== 'custom_text_value') {
          (updatedTextConfig as any)[k] = false;
        }
      });
    } else if (key !== 'custom_text' && key !== 'custom_text_value' && value === true) {
      // If any other option is enabled, disable custom_text
      updatedTextConfig.custom_text = false;
    }
    
    updateWatermark({ text_config: updatedTextConfig });
  };

  if (!isOpen) return null;

  const watermarkText = buildWatermarkText(localWatermark.text_config) || 'No watermark text configured';
  const angle = typeof localWatermark.angle === 'number' ? localWatermark.angle : 0;
  // Invert angle for correct visual direction:
  // - Vertical (90°): top part should look left, so we use -90°
  // - Diagonal (45°): should be bottom-left to top-right, so we use -45°
  const invertedAngle = -angle;
  
  // Use actual config values (not multiplied)
  const fontSize = localWatermark.font_size || 12;
  const color = localWatermark.color || '#000000';
  const opacity = localWatermark.opacity || 0.5;
  const fontFamily = localWatermark.font || 'Arial';

  // Calculate vertical spacing between rows
  // Fixed at 50mm for consistent spacing
  const spacingY = 50 * 3.78; // 50mm converted to px (1mm = 3.78px at 96dpi)

  // A4 dimensions: Portrait = 210mm x 297mm, Landscape = 297mm x 210mm
  const a4Width = orientation === 'portrait' ? 210 : 297; // mm
  const a4Height = orientation === 'portrait' ? 297 : 210; // mm
  const mmToPx = 3.78; // Approximate conversion at 96dpi
  const previewWidthPx = a4Width * mmToPx;
  const previewHeightPx = a4Height * mmToPx;

  const sidebarWidth = 350; // Increased from 256px
  const modalRoot = document.getElementById('modal-root') || document.body;

  return createPortal(
    <>
      {/* Sidebar - Desktop: left side, Mobile: bottom drawer */}
      <div 
        className={`fixed bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 z-[10000] overflow-y-auto ${
          isMobile 
            ? 'bottom-0 left-0 right-0 top-auto max-h-[60vh] border-t shadow-lg rounded-t-lg' 
            : 'left-0 top-14 bottom-0 border-r'
        }`}
        style={isMobile ? {} : { width: `${sidebarWidth}px` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 space-y-6">
          {isMobile && (
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Preview Settings
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-1"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          )}
          {!isMobile && (
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Preview Settings
            </h3>
          )}
          
          {/* Orientation Tabs */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setOrientation('portrait')}
              className={`flex-1 p-2 rounded-md transition-all flex items-center justify-center gap-2 ${
                orientation === 'portrait'
                  ? 'bg-white dark:bg-gray-600 text-governate-600 dark:text-governate-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <DocumentTextIcon className="w-5 h-5" />
              <span className="text-sm">Portrait</span>
            </button>
            <button
              onClick={() => setOrientation('landscape')}
              className={`flex-1 p-2 rounded-md transition-all flex items-center justify-center gap-2 ${
                orientation === 'landscape'
                  ? 'bg-white dark:bg-gray-600 text-governate-600 dark:text-governate-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <RectangleStackIcon className="w-5 h-5" />
              <span className="text-sm">Landscape</span>
            </button>
          </div>

          {/* Text Configuration - First */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('watermark.displayItems')}
            </label>
            <div className="space-y-3 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
              {/* Radio buttons for text mode */}
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="text-mode"
                    checked={!localWatermark.text_config.custom_text}
                    onChange={() => {
                      const updatedTextConfig = { ...localWatermark.text_config, custom_text: false };
                      updateWatermark({ text_config: updatedTextConfig });
                    }}
                    className="mr-2 text-governate-600 focus:ring-governate-500"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.combination')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="text-mode"
                    checked={localWatermark.text_config.custom_text || false}
                    onChange={() => {
                      const updatedTextConfig = {
                        ...localWatermark.text_config,
                        custom_text: true,
                        owner_company_name: false,
                        recipient_company_name: false,
                        owner_email: false,
                        recipient_email: false,
                        recipient_ip_address: false,
                        timestamp: false,
                        file_name: false,
                        device_name: false,
                      };
                      updateWatermark({ text_config: updatedTextConfig });
                    }}
                    className="mr-2 text-governate-600 focus:ring-governate-500"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.customText')}</span>
                </label>
              </div>

              {/* Show checkboxes only when Combination is selected */}
              {!localWatermark.text_config.custom_text && (
                <div className="space-y-2 max-h-48 overflow-y-auto pt-2 border-t border-gray-200 dark:border-gray-700">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localWatermark.text_config.owner_company_name || false}
                      onChange={(e) => handleTextConfigChange('owner_company_name', e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.ownerCompanyName')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localWatermark.text_config.recipient_company_name || false}
                      onChange={(e) => handleTextConfigChange('recipient_company_name', e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.recipientCompanyName')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localWatermark.text_config.owner_email || false}
                      onChange={(e) => handleTextConfigChange('owner_email', e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.ownerEmail')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localWatermark.text_config.recipient_email || false}
                      onChange={(e) => handleTextConfigChange('recipient_email', e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.recipientEmail')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localWatermark.text_config.recipient_ip_address || false}
                      onChange={(e) => handleTextConfigChange('recipient_ip_address', e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.recipientIpAddress')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localWatermark.text_config.timestamp || false}
                      onChange={(e) => handleTextConfigChange('timestamp', e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.timestamp')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localWatermark.text_config.file_name || false}
                      onChange={(e) => handleTextConfigChange('file_name', e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.fileName')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localWatermark.text_config.device_name || false}
                      onChange={(e) => handleTextConfigChange('device_name', e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{t('watermark.deviceName')}</span>
                  </label>
                </div>
              )}

              {/* Show custom text input only when Custom Text is selected */}
              {localWatermark.text_config.custom_text && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <input
                    type="text"
                    value={localWatermark.text_config.custom_text_value || ''}
                    onChange={(e) => handleTextConfigChange('custom_text_value', e.target.value)}
                    placeholder={t('watermark.customTextPlaceholder')}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Angle */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('watermark.rotationAngle')}
            </label>
            <AlignmentControl
              value={angle}
              onChange={(newAngle) => updateWatermark({ angle: newAngle })}
            />
          </div>

          {/* Font Size */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('watermark.fontSize')}
            </label>
            <input
              type="number"
              value={localWatermark.font_size || 12}
              onChange={(e) => updateWatermark({ font_size: parseInt(e.target.value) || 12 })}
              min="8"
              max="72"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Font */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('watermark.font')}
            </label>
            <select
              value={localWatermark.font || 'Arial'}
              onChange={(e) => updateWatermark({ font: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent text-sm"
            >
              <option value="Arial">{t('watermark.fontArial')}</option>
              <option value="Times New Roman">{t('watermark.fontTimesNewRoman')}</option>
              <option value="Courier New">{t('watermark.fontCourierNew')}</option>
              <option value="Helvetica">{t('watermark.fontHelvetica')}</option>
              <option value="Verdana">{t('watermark.fontVerdana')}</option>
            </select>
          </div>

          {/* Color Palette - Wider */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('watermark.color')}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={color}
                onChange={(e) => updateWatermark({ color: e.target.value })}
                className="h-12 w-24 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => updateWatermark({ color: e.target.value })}
                placeholder="#000000"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Opacity */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('watermark.opacityLabel', { value: opacity })}
            </label>
            <input
              type="range"
              value={opacity}
              onChange={(e) => updateWatermark({ opacity: parseFloat(e.target.value) })}
              min="0"
              max="1"
              step="0.1"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Backdrop and Modal */}
      <div 
        className={`fixed bg-black bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center z-[9999] backdrop-blur-sm ${
          isMobile 
            ? 'top-14 left-0 right-0 bottom-0' 
            : 'top-14 left-0 right-0 bottom-0'
        }`}
        style={isMobile ? {} : { left: `${sidebarWidth}px` }}
        onClick={onClose}
      >
        <div
          ref={modalRef}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col relative ${
            isMobile ? 'w-full h-full m-4' : ''
          }`}
          style={isMobile ? {} : {
            width: `${previewWidthPx}px`,
            height: `${previewHeightPx}px`,
            maxWidth: `calc(100vw - ${sidebarWidth}px - 48px)`,
            maxHeight: 'calc(100vh - 80px)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button - top right corner (desktop only, mobile has it in drawer) */}
          {!isMobile && (
            <button
              onClick={onClose}
              className="absolute top-2 right-2 z-10 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}

          {/* Preview Area */}
          <div className="flex-1 relative overflow-hidden bg-gray-100 dark:bg-gray-800 p-8 flex items-center justify-center">
            <div
              className="relative border-2 border-gray-300 dark:border-gray-600 bg-white shadow-inner"
              style={{
                width: `${previewWidthPx}px`,
                height: `${previewHeightPx}px`,
              }}
            >
              {/* Repeating Watermark Pattern */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{
                  fontFamily: fontFamily,
                  fontSize: `${fontSize}px`,
                  color: color,
                  opacity: opacity,
                }}
              >
                {/* Create a properly spaced grid of repeating watermarks */}
                {(() => {
                  // Normalize spacing around pipe separators (single space before and after)
                  const normalizedText = watermarkText.replace(/\s*\|\s*/g, ' | ').trim();
                  
                  if (!normalizedText) {
                    return null;
                  }
                  
                  // For rotated text, we need to account for the rotation angle
                  const rotationRad = Math.abs(invertedAngle) * Math.PI / 180;
                  
                  // Estimate text width (rough approximation)
                  const avgCharWidth = fontSize * 0.6; // Approximate character width
                  const textWidth = normalizedText.length * avgCharWidth;
                  
                  // Calculate how many times the text can fit across the page width
                  // Account for rotation - when rotated, we need more width
                  const baseWidth = previewWidthPx;
                  const repetitionsPerLine = Math.max(10, Math.floor(baseWidth / textWidth) * 2); // Ensure enough repetitions
                  
                  // Create repeated text for each line
                  const repeatedText = normalizedText + ' | ';
                  const fullLineText = repeatedText.repeat(repetitionsPerLine);
                  
                  // Calculate how many rows we need to cover the entire area including corners
                  // For rotated text, we need extra rows to cover corners
                  const diagonalHeight = Math.sqrt(previewWidthPx * previewWidthPx + previewHeightPx * previewHeightPx);
                  const rows = Math.ceil(diagonalHeight / spacingY) + 20; // Extra rows to ensure corner coverage
                  
                  // Calculate starting offset to ensure coverage from top-left corner
                  // For 45° rotation, we need to start from negative Y to cover top corners
                  // For -45° rotation, we need to start from negative Y to cover top corners
                  const startOffsetY = -Math.abs(Math.sin(rotationRad) * previewWidthPx * 0.5) - spacingY * 2;
                  
                  return Array.from({ length: rows }).map((_, row) => {
                    const topPosition = startOffsetY + row * spacingY;
                    return (
                      <div
                        key={row}
                        style={{
                          position: 'absolute',
                          left: '0',
                          top: `${topPosition}px`,
                          transform: `rotate(${invertedAngle}deg)`,
                          transformOrigin: '0 0', // Rotate from left edge
                          width: `${previewWidthPx * 2.5}px`, // Wide enough to cover rotated area
                          whiteSpace: 'nowrap', // Don't wrap - just cut off
                          overflow: 'hidden', // Hide overflow
                          textOverflow: 'clip', // Clip text that doesn't fit
                          lineHeight: `${fontSize * 1.2}px`, // Line height based on font size
                          textAlign: 'left', // Start from left
                          fontKerning: 'normal', // Prevent extra spacing
                          letterSpacing: '0', // No extra letter spacing
                          wordSpacing: 'normal', // Normal word spacing
                        }}
                      >
                        {fullLineText}
                      </div>
                  );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    modalRoot
  );
};

export default WatermarkPreview;
